import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Plus } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import useAddresses from '../../hooks/useAddresses';
import PageHeader from '../../components/ui/PageHeader';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import AddressCard from '../../components/AddressCard';
import AddressFormModal from '../../components/AddressFormModal';
import MapAddressPicker from '../../components/MapAddressPicker';
import DateTimeSlotPicker from '../../components/DateTimeSlotPicker';
import CountSelector from '../../components/CountSelector';

const emptyGuestAddress = { location: '', address_line: '', building: '', unit: '', latitude: null, longitude: null };

export default function NewOrder() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isGuest = !user;
  const [searchParams] = useSearchParams();
  const listingId = searchParams.get('listing');

  const [listing, setListing] = useState(null);
  const [message, setMessage] = useState('');
  const [answers, setAnswers] = useState({});
  const [scheduledAt, setScheduledAt] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [guestFirstName, setGuestFirstName] = useState('');
  const [guestLastName, setGuestLastName] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestAddress, setGuestAddress] = useState(emptyGuestAddress);

  // "Date & time" is now a dedicated field (see DateTimeSlotPicker below), not a bank question —
  // filter out any legacy selection from listings configured before that change.
  const questions = (listing?.listing_questions || []).filter(lq => lq.question.type !== 'datetime');

  const setAnswer = (questionId, value) => {
    setAnswers(a => ({ ...a, [questionId]: value }));
  };

  const {
    addresses,
    loading: addressesLoading,
    createAddress,
    updateAddress,
  } = useAddresses();
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [addressModalOpen, setAddressModalOpen] = useState(false);

  // Fetch listing
  useEffect(() => {
    if (!listingId) return navigate('/listings');
    axios.get(`${process.env.REACT_APP_API_URL}/api/listings/${listingId}`)
      .then(res => setListing(res.data.listing))
      .catch(() => navigate('/listings'));
  }, [listingId, navigate]);

  // Pre-select the default address once the saved-address list loads
  useEffect(() => {
    if (selectedAddressId || addresses.length === 0) return;
    const defaultAddress = addresses.find(a => a.is_default) || addresses[0];
    setSelectedAddressId(defaultAddress.id);
  }, [addresses, selectedAddressId]);

  const handleAddressSaved = (address) => {
    setSelectedAddressId(address.id);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    let addressPayload;
    if (isGuest) {
      if (!guestFirstName.trim() || !guestLastName.trim() || !guestPhone.trim() || !guestEmail.trim()) {
        setError('Please fill in your first name, last name, phone number, and email.');
        return;
      }
      if (!guestAddress.address_line.trim()) {
        setError('Please set your delivery location on the map and enter an address line.');
        return;
      }
      addressPayload = guestAddress;
    } else {
      const selectedAddress = addresses.find(a => a.id === selectedAddressId);
      if (!selectedAddress) {
        setError('Please choose or add a delivery address.');
        return;
      }
      addressPayload = selectedAddress;
    }

    if (!scheduledAt) {
      setError('Please choose a service date and time.');
      return;
    }

    for (const lq of questions) {
      const value = answers[lq.question.id];
      if (lq.required && (value === undefined || value === null || value === '')) {
        setError(`Please answer: ${lq.question.label}`);
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        listing_id: listingId,
        message,
        address_line: addressPayload.address_line,
        building: addressPayload.building,
        unit: addressPayload.unit,
        latitude: addressPayload.latitude,
        longitude: addressPayload.longitude,
        scheduled_at: scheduledAt,
        answers: Object.entries(answers)
          .filter(([, value]) => value !== undefined && value !== null && value !== '')
          .map(([question_id, value]) => ({ question_id, value })),
      };

      let headers;
      if (isGuest) {
        payload.guest_first_name = guestFirstName.trim();
        payload.guest_last_name = guestLastName.trim();
        payload.guest_phone = guestPhone.trim();
        payload.guest_email = guestEmail.trim();
        headers = {};
      } else {
        headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      }

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/orders`,
        payload,
        { headers }
      );
      navigate(isGuest ? `/orders/track/${res.data.order.guest_token}` : `/orders/${res.data.order.id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!listing) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div role="status" aria-label="Loading" className="w-8 h-8 border-2 border-sand-200 border-t-terra-400 rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="bg-sand-50 min-h-screen pb-24 sm:pb-0">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <PageHeader title="Send Request" subtitle="Fill in your delivery details and an optional message" />

        {/* Listing preview */}
        <div className="bg-white border border-sand-200 rounded-lg p-4 flex gap-4 items-start mb-8 shadow-card">
          <img
            src={listing.cover_photo_url || listing.cover_photo}
            alt={listing.title}
            className="w-16 h-16 rounded-lg object-cover shrink-0 bg-sand-100"
          />
          <div>
            <p className="font-heading text-sm font-semibold text-ink-900">{listing.title}</p>
            <p className="text-xs text-ink-400 mt-0.5">{listing.category}</p>
            <p className="text-sm font-semibold text-terra-400 mt-1">AED {listing.price}</p>
          </div>
        </div>

        <form id="new-order-form" onSubmit={handleSubmit} className="flex flex-col gap-5">
          {isGuest && (
            <div className="flex flex-col gap-4">
              <div className="flex gap-3">
                <Input
                  id="guest-first-name"
                  label="First name"
                  value={guestFirstName}
                  onChange={e => setGuestFirstName(e.target.value)}
                  required
                  className="flex-1"
                />
                <Input
                  id="guest-last-name"
                  label="Last name"
                  value={guestLastName}
                  onChange={e => setGuestLastName(e.target.value)}
                  required
                  className="flex-1"
                />
              </div>
              <Input
                id="guest-phone"
                label="Phone number"
                type="tel"
                value={guestPhone}
                onChange={e => setGuestPhone(e.target.value)}
                placeholder="e.g. 05XXXXXXXX"
                required
              />
              <Input
                id="guest-email"
                label="Email"
                type="email"
                value={guestEmail}
                onChange={e => setGuestEmail(e.target.value)}
                placeholder="you@example.com"
                required
              />
            </div>
          )}

          {/* Delivery Address */}
          <div>
            <label className="text-sm font-medium text-ink-700 block mb-2">
              Delivery Address
            </label>

            {isGuest ? (
              <div className="flex flex-col gap-3">
                <MapAddressPicker
                  value={guestAddress.latitude != null ? { latitude: guestAddress.latitude, longitude: guestAddress.longitude } : null}
                  onChange={coords => setGuestAddress(a => ({ ...a, ...coords, address_line: '' }))}
                  onResolvedAddress={resolved => setGuestAddress(a => ({
                    ...a,
                    address_line: resolved.address_line || a.address_line,
                    location: resolved.location,
                  }))}
                />
                <Input
                  id="guest-address-line"
                  label="Address line"
                  value={guestAddress.address_line}
                  onChange={e => setGuestAddress(a => ({ ...a, address_line: e.target.value }))}
                  placeholder="Street / area name"
                  required
                />
                <div className="flex gap-3">
                  <Input
                    id="guest-building"
                    label="Building"
                    value={guestAddress.building}
                    onChange={e => setGuestAddress(a => ({ ...a, building: e.target.value }))}
                    className="flex-1"
                  />
                  <Input
                    id="guest-unit"
                    label="Unit / Flat"
                    value={guestAddress.unit}
                    onChange={e => setGuestAddress(a => ({ ...a, unit: e.target.value }))}
                    className="flex-1"
                  />
                </div>
              </div>
            ) : addressesLoading ? (
              <div className="flex items-center justify-center py-6">
                <div className="w-6 h-6 border-2 border-sand-200 border-t-terra-400 rounded-full animate-spin" />
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {addresses.map(address => (
                  <AddressCard
                    key={address.id}
                    address={address}
                    selectable
                    selected={selectedAddressId === address.id}
                    onSelect={() => setSelectedAddressId(address.id)}
                  />
                ))}

                <button
                  type="button"
                  onClick={() => setAddressModalOpen(true)}
                  className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-sand-300 text-sm font-medium text-ink-700 hover:border-terra-400 hover:text-terra-400 transition-all duration-200 py-3"
                >
                  <Plus className="w-4 h-4" /> Add new address
                </button>
              </div>
            )}
          </div>

          <DateTimeSlotPicker onChange={setScheduledAt} required />

          {questions.map(lq => {
            const q = lq.question;
            const value = answers[q.id];
            const label = `${q.label}${lq.required ? '' : ' (optional)'}`;

            if (q.type === 'number') {
              return (
                <CountSelector
                  key={q.id}
                  label={q.unit ? `${q.label} — ${q.unit}` : q.label}
                  unit={q.unit}
                  value={value === undefined ? null : value}
                  onChange={v => setAnswer(q.id, v)}
                  required={lq.required}
                />
              );
            }

            if (q.type === 'boolean') {
              return (
                <div key={q.id}>
                  <label className="text-sm font-medium text-ink-700 block mb-1">{label}</label>
                  <select
                    value={value === undefined ? '' : String(value)}
                    onChange={e => setAnswer(q.id, e.target.value === '' ? undefined : e.target.value === 'true')}
                    required={lq.required}
                    className="w-full px-4 py-2.5 rounded-md border border-sand-200 bg-white text-ink-900 text-sm focus:outline-none focus:ring-2 focus:ring-terra-400 focus:border-terra-400 transition-all duration-200"
                  >
                    <option value="">Select…</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>
              );
            }

            return (
              <Input
                key={q.id}
                id={`q-${q.id}`}
                label={label}
                as="textarea"
                rows={2}
                value={value ?? ''}
                onChange={e => setAnswer(q.id, e.target.value)}
                required={lq.required}
              />
            );
          })}

          <Input
            id="message"
            label="Message (optional)"
            as="textarea"
            rows={4}
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder="Any details for the provider…"
          />

          {error && <p className="text-sm text-uae-red">{error}</p>}

          {/* Desktop CTA */}
          <div className="hidden sm:block">
            <Button type="submit" variant="primary" loading={loading} className="w-full justify-center">
              Send Request
            </Button>
          </div>
        </form>
      </div>

      {/* Mobile fixed CTA */}
      <div className="fixed bottom-0 inset-x-0 z-30 bg-white border-t border-sand-200 p-4 sm:hidden">
        <Button type="submit" form="new-order-form" variant="primary" loading={loading} className="w-full justify-center">
          Send Request
        </Button>
      </div>

      <AddressFormModal
        isOpen={addressModalOpen}
        onClose={() => setAddressModalOpen(false)}
        address={null}
        forceDefault={addresses.length === 0}
        createAddress={createAddress}
        updateAddress={updateAddress}
        onSaved={handleAddressSaved}
      />
    </div>
  );
}
